"use client"

import { deleteLineItem, updateLineItem } from "@lib/data/cart"
import { HttpTypes } from "@medusajs/types"
import AmzPrice from "@modules/amz/components/price"
import ErrorMessage from "@modules/checkout/components/error-message"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import Spinner from "@modules/common/icons/spinner"
import Image from "next/image"
import { useState } from "react"

const AmzCartItem = ({
  item,
  currencyCode,
}: {
  item: HttpTypes.StoreCartLineItem
  currencyCode: string
}) => {
  const [updating, setUpdating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const run = async (fn: () => Promise<unknown>) => {
    setError(null)
    setUpdating(true)
    await fn()
      .catch((err) => setError(err.message))
      .finally(() => setUpdating(false))
  }

  const variantTitle = item.variant?.title ?? item.variant_title
  const options = variantTitle && !["Estandar", "Estándar"].includes(variantTitle) ? [variantTitle] : []

  return (
    <li className="flex gap-x-4 py-5 border-b border-zl-line" data-testid="product-row">
      <LocalizedClientLink
        href={`/products/${item.product_handle}`}
        className="relative w-24 h-24 small:w-36 small:h-36 shrink-0 bg-zl-surface border border-zl-line"
      >
        {item.thumbnail && (
          <Image src={item.thumbnail} alt="" fill sizes="180px" className="object-contain p-2" />
        )}
      </LocalizedClientLink>
      <div className="flex-1 min-w-0 flex flex-col gap-y-1">
        <div className="flex justify-between gap-x-4">
          <LocalizedClientLink
            href={`/products/${item.product_handle}`}
            className="font-display text-lg font-medium leading-6 tracking-[-0.01em] line-clamp-2 hover:text-zl-red"
            data-testid="product-title"
          >
            {item.product_title}
          </LocalizedClientLink>
          <span className="hidden small:block shrink-0 font-bold">
            <AmzPrice amount={item.total ?? 0} currencyCode={currencyCode} size="sm" />
          </span>
        </div>
        <span className="zl-badge text-zl-ink"><span className="text-zl-green">&#9679;</span> En stock</span>
        {options.length > 0 && (
          <span className="zl-badge text-zl-steel" data-testid="product-variant">
            {options.join(" · ")}
          </span>
        )}
        <span className="small:hidden font-bold">
          <AmzPrice amount={item.total ?? 0} currencyCode={currencyCode} size="sm" />
        </span>
        <div className="flex items-center gap-x-3 mt-2 text-xs">
          <select
            value={item.quantity}
            onChange={(e) => run(() => updateLineItem({ lineId: item.id, quantity: Number(e.target.value) }))}
            className="bg-white border border-zl-ink px-2 h-8 font-mono text-xs shadow-inset"
            disabled={updating}
            data-testid="product-select-button"
            aria-label="Cantidad"
          >
            {Array.from({ length: Math.max(10, item.quantity) }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>
                Cant.: {n}
              </option>
            ))}
          </select>
          <span className="text-zl-line">|</span>
          <button
            className="zl-label text-zl-steel hover:text-zl-red"
            onClick={() => run(() => deleteLineItem(item.id))}
            disabled={updating}
            data-testid="product-delete-button"
          >
            Eliminar
          </button>
          {updating && <Spinner />}
        </div>
        <ErrorMessage error={error} data-testid="product-error-message" />
      </div>
    </li>
  )
}

export default AmzCartItem
